import { createSlice } from '@reduxjs/toolkit';
import { incidentsApi } from '../functions/IncidentsApi';
import { toast } from 'react-toastify';

const initialState = {
  incidentsList: [],
  incidentsPagination: null,
  loading: false,
  selectedIncidentInfo: null,
  incidentInfoFetching: null,
  formSettings: {},
  formSettingsFetching: false,
  formSaveFetching: false,
  departmentsList: {},
  employeesList: [],
  callStatus: [],
};

export const incidentsReducer = createSlice({
  name: 'incidentsReducer',
  initialState,
  reducers: {
    setIncidentsList: (state, action) => {
      state.incidentsList = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setPagination: (state, action) => {
      state.incidentsPagination = action.payload;
    },
    setSelectedIncidentInfo: (state, action) => {
      state.selectedIncidentInfo = action.payload;
    },
    setIncidentInfoFetching: (state, action) => {
      state.incidentInfoFetching = action.payload;
    },
    setDeleteIncident: (state, action) => {
      state.incidentsList = state.incidentsList.filter(
        (item) => item.incident_id !== action.payload
      );
      state.incidentsPagination = {
        ...state.incidentsPagination,
        total: state.incidentsPagination.total - 1,
      };
    },
    setFormSettings: (state, action) => {
      state.formSettings = action.payload;
    },
    setFormSettingsFetching: (state, action) => {
      state.formSettingsFetching = action.payload;
    },
    setFormSaveFetching: (state, action) => {
      state.formSaveFetching = action.payload;
    },
    setDepartmentsList: (state, action) => {
      state.departmentsList = action.payload;
    },
    setIsHasNote: (state, action) => {
      state.incidentsList = state.incidentsList.map((item) => {
        if (item.incident_id === action.payload) {
          return { ...item, has_note: true };
        }
        return item;
      });
    },
    setIsHasPropertyDamage: (state, action) => {
      state.incidentsList = state.incidentsList.map((item) => {
        if (item.incident_id === action.payload.id) {
          return { ...item, property_damage: action.payload.isHasProperty };
        }
        return item;
      });
    },
    setEmployeesList: (state, action) => {
      state.employeesList = action.payload;
    },
    setCallStatusList: (state, action) => {
      state.callStatus = action.payload;
    },
  },
});

export const {
  setIncidentsList,
  setLoading,
  setPagination,
  setSelectedIncidentInfo,
  setIncidentInfoFetching,
  setDeleteIncident,
  setFormSettings,
  setFormSettingsFetching,
  setFormSaveFetching,
  setDepartmentsList,
  setIsHasNote,
  setEmployeesList,
  setCallStatusList,
  setIsHasPropertyDamage,
} = incidentsReducer.actions;

export default incidentsReducer.reducer;

export const getIncidentsList =
  ({
    page,
    searchQuery,
    start_dt,
    end_dt,
    pageSize,
    type,
    status,
    nerisStatus,
    department,
    responseTime,
    employee,
    form_status,
  }) =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));

      const response = await incidentsApi.getIncidents({
        page,
        searchQuery,
        start_dt,
        end_dt,
        pageSize,
        type,
        status,
        nerisStatus,
        responseTime,
        department,
        employee,
        form_status,
      });

      if (response.status === 200) {
        dispatch(setIncidentsList(response.data.data));
        dispatch(setPagination(response.data.pagination));
      } else throw response.response.data;
    } catch (err) {
      toast.error(err.message);
      console.error(err);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const getIncidentInfo = (id) => async (dispatch) => {
  try {
    dispatch(setIncidentInfoFetching(true));
    const response = await incidentsApi.getIncidentInfo(id);

    if (response.status === 200) {
      dispatch(setSelectedIncidentInfo({ ...response.data, id }));
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.error(err);
  } finally {
    dispatch(setIncidentInfoFetching(false));
  }
};

export const uploadIncidentXml = (selectedFile) => async (dispatch) => {
  try {
    const formData = new FormData();
    formData.append('file', selectedFile);

    const response = await incidentsApi.uploadIncidentXml(formData);

    if (response.status === 200) {
      return true;
    } else throw response;
  } catch (err) {
    return err;
  }
};

export const deleteIncident = (id) => async (dispatch) => {
  try {
    const response = await incidentsApi.deleteIncident(id);
    if (response.status === 200 && response.data.status === 'success') {
      toast.success('The incident has been successfully removed.');
      dispatch(setDeleteIncident(id));
    } else throw response;
  } catch (err) {
    toast.error('Failed to delete Incident');
    console.log(err);
  }
};

export const syncNeris = (id) => async (dispatch, getState) => {
  try {
    const response = await incidentsApi.syncNeris(id);
    if (response.status === 200 && response.data.status === 'success') {
      toast.success('Synchronization with neris was successful');
      return true;
      // const { incidentsPagination } = getState().incidentsReducer;
      // const currentPage = incidentsPagination ? incidentsPagination.current_page : 1;
      // const limit = incidentsPagination ? incidentsPagination.per_page : 20;
      // dispatch(getIncidentsList(currentPage, null, null, null, limit, null, null, null, null));
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  }
};

export const getFormSettings = (id) => async (dispatch) => {
  try {
    dispatch(setFormSettingsFetching(true));
    const response = await incidentsApi.formSettings({
      incident: id,
    });
    if (response.status === 200) {
      dispatch(setFormSettings(response.data));
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  } finally {
    dispatch(setFormSettingsFetching(false));
  }
};

export const saveIncidentsForm =
  ({ id, data, successCb, errorCb }) =>
  async (dispatch) => {
    try {
      dispatch(setFormSaveFetching(true));
      const response = await incidentsApi.saveIncidentsForm({ id, data });
      if (response.status === 200) {
        successCb?.(response);
      } else throw response.response.data;
    } catch (err) {
      toast.error(err.message);
      console.log(err);
      errorCb?.(err);
    } finally {
      dispatch(setFormSaveFetching(false));
    }
  };

export const getDepartmentsList = (id) => async (dispatch) => {
  try {
    dispatch(setFormSettingsFetching(true));
    const response = await incidentsApi.getDepartmentsList(id);
    if (response.status === 200) {
      dispatch(setDepartmentsList(response.data.departments));
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  } finally {
    dispatch(setFormSettingsFetching(false));
  }
};

export const saveNote = (incidentId, note, noteId) => async (dispatch) => {
  try {
    const response = await incidentsApi.saveNotes(incidentId, note, noteId);
    if (response.status === 200) {
      toast.success('Note saved successfully');
      dispatch(setIsHasNote(incidentId));
      return true;
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  }
};

export const savePropertyDamage = (id, data) => async (dispatch) => {
  try {
    const response = await incidentsApi.savePropertyDamage(id, data);
    if (response.status === 200) {
      toast.success('Property damage saved successfully');
      const isHasProperty = !!data.properties.length > 0;
      dispatch(setIsHasPropertyDamage({ id, isHasProperty }));
      return true;
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  }
};

export const getEmployeesList = (params) => async (dispatch) => {
  try {
    const response = await incidentsApi.getEmployees(params);

    if (response.status === 200) {
      if (response.data.data && Object.entries(response.data.data).length > 0) {
        const transform = Object.entries(response.data.data).map(
          ([id, { name }]) => ({
            label: name,
            id,
          })
        );
        dispatch(setEmployeesList(transform));
      }
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  }
};

export const getCallStatus = () => async (dispatch) => {
  try {
    const response = await incidentsApi.getCallStatus();

    if (response.status === 200) {
      if (response.data.data && Object.entries(response.data.data).length > 0) {
        const transform = Object.entries(response.data.data).map(
          ([id, { name }]) => ({
            label: name,
            value: id,
          })
        );
        dispatch(setCallStatusList(transform));
      }
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  }
};

export const changeIncidentEmployee =
  (incidentId, emloyeeId, requestForIncidents) => async (dispatch) => {
    try {
      const response = await incidentsApi.changeIncidentEmployee(
        incidentId,
        emloyeeId
      );

      if (response.status === 200) {
        requestForIncidents();
        toast.success(
          `Officer in Charge successfully ${response.data.message?.toLowerCase()}`
        );
      } else throw response.response.data;
    } catch (err) {
      toast.error(err.message);
      console.log(err);
    }
  };
